import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function AdminSurveyDetail() {
  const { id } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingEmail, setEditingEmail] = useState(null);
  const [emailValue, setEmailValue] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSurvey = useCallback(() => {
    api.get(`/admin/surveys/${id}`)
      .then((r) => setSurvey(r.data?.data))
      .catch(() => setSurvey(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSurvey]);

  const addToken = async () => {
    setActionLoading(true);
    try {
      await api.post(`/admin/surveys/${id}/tokens`);
      toast.success('Token ajouté');
      fetchSurvey();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteToken = async (token) => {
    if (token.has_voted) {
      toast.error('Impossible de supprimer un token déjà utilisé');
      return;
    }
    if (!window.confirm('Supprimer ce token non utilisé ?')) return;
    setActionLoading(true);
    try {
      await api.delete(`/admin/surveys/${id}/tokens/${token.token}`);
      toast.success('Token supprimé');
      fetchSurvey();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const saveEmail = async (token) => {
    const email = emailValue.trim();
    if (!email) {
      toast.error('Email requis');
      return;
    }
    setActionLoading(true);
    try {
      await api.patch(`/admin/surveys/${id}/tokens/${token.token}`, { email });
      toast.success('Email rattaché');
      setEditingEmail(null);
      setEmailValue('');
      fetchSurvey();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const removeEmail = async (token) => {
    setActionLoading(true);
    try {
      await api.delete(`/admin/surveys/${id}/tokens/${token.token}/email`);
      toast.success('Email retiré');
      fetchSurvey();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const exportLinks = () => {
    if (!survey?.tokens?.length) return;
    const base = window.location.origin;
    const lines = survey.tokens.map((t) => `${base}/s/${t.token}`);
    const text = lines.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sondage-${survey.id}-liens.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export TXT téléchargé');
  };

  const exportForMailing = () => {
    if (!survey?.tokens?.length) return;
    const base = window.location.origin;
    const header = 'email;lien\n';
    const rows = survey.tokens.map((t) => `${t.email || ''};${base}/s/${t.token}`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sondage-${survey.id}-emails-liens.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export CSV (emails + liens) téléchargé');
  };

  const copyLinks = () => {
    if (!survey?.tokens?.length) return;
    const base = window.location.origin;
    const text = survey.tokens.map((t) => `${base}/s/${t.token}`).join('\n');
    navigator.clipboard.writeText(text).then(() => toast.success('Liens copiés !'));
  };

  if (loading) return <p className="text-gray-500">Chargement...</p>;
  if (!survey) return <p className="text-red-600">Sondage non trouvé.</p>;

  const statusLabel = { draft: 'Brouillon', active: 'Actif', closed: 'Clôturé' };
  const statusColor = { draft: 'bg-gray-200', active: 'bg-success-500 text-white', closed: 'bg-gray-400 text-white' };

  return (
    <div>
      <Link to="/admin/surveys" className="text-primary-600 hover:underline block mb-4">← Sondages</Link>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{survey.title}</h1>
          <span className={`inline-block px-2 py-1 rounded text-sm ${statusColor[survey.status]}`}>
            {statusLabel[survey.status]}
          </span>
          <p className="text-gray-500 mt-1">{survey.date_start} — {survey.date_end}</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/admin/surveys/${id}/edit`}>
            <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Modifier</button>
          </Link>
          <Link to={`/admin/surveys/${id}/report`}>
            <button className="px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600">Rapport</button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Participation</p>
          <p className="text-xl font-bold">{survey.participations ?? 0} / {survey.tokens?.length ?? 0}</p>
          <p className="text-sm text-gray-500">{survey.participation_rate ?? 0}%</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">Gestion des tokens et liens</h2>
        <p className="text-sm text-gray-600 mb-3">
          Rattachez un email à chaque token pour envoyer les liens aux votants. Un token ne peut être utilisé qu&apos;une seule fois.
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={addToken}
            disabled={actionLoading}
            className="px-4 py-2 bg-success-500 text-white rounded-lg hover:bg-success-600 disabled:opacity-50"
          >
            + Ajouter un token
          </button>
          <button
            onClick={exportLinks}
            disabled={!survey.tokens?.length}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            Export TXT
          </button>
          <button
            onClick={exportForMailing}
            disabled={!survey.tokens?.length}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            Export CSV (emails + liens)
          </button>
          <button
            onClick={copyLinks}
            disabled={!survey.tokens?.length}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Copier les liens
          </button>
        </div>
        {survey.tokens?.length > 0 && (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[400px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Statut</th>
                  <th className="text-left px-3 py-2">Lien</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {survey.tokens.map((t) => (
                  <tr key={t.token} className="border-t">
                    <td className="px-3 py-2">
                      {editingEmail === t.token ? (
                        <div className="flex gap-1 items-center">
                          <input
                            type="email"
                            value={emailValue}
                            onChange={(e) => setEmailValue(e.target.value)}
                            placeholder="email@exemple.fr"
                            className="flex-1 px-2 py-1 border rounded text-sm"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => saveEmail(t)}
                            disabled={actionLoading}
                            className="px-2 py-1 bg-primary-600 text-white rounded text-xs"
                          >
                            OK
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditingEmail(null); setEmailValue(''); }}
                            className="px-2 py-1 text-gray-500 text-xs"
                          >
                            Annuler
                          </button>
                        </div>
                      ) : (
                        <span className={t.email ? '' : 'text-gray-400'}>
                          {t.email || '—'}
                          {t.email && (
                            <>
                              <button
                                type="button"
                                onClick={() => { setEditingEmail(t.token); setEmailValue(t.email); }}
                                disabled={actionLoading}
                                className="ml-1 text-primary-600 text-xs hover:underline"
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                onClick={() => removeEmail(t)}
                                disabled={actionLoading}
                                className="ml-1 text-danger-500 text-xs hover:underline"
                              >
                                Retirer
                              </button>
                            </>
                          )}
                          {!t.email && (
                            <button
                              type="button"
                              onClick={() => { setEditingEmail(t.token); setEmailValue(''); }}
                              disabled={actionLoading}
                              className="ml-1 text-primary-600 text-xs hover:underline"
                            >
                              Rattacher
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${t.has_voted ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-600'}`}>
                        {t.has_voted ? '✓ A voté' : 'En attente'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <a
                        href={`/s/${t.token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:underline truncate max-w-[180px] block"
                      >
                        …/s/{t.token.slice(0, 8)}…
                      </a>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!t.has_voted && (
                        <button
                          type="button"
                          onClick={() => deleteToken(t)}
                          disabled={actionLoading}
                          className="text-danger-500 text-xs hover:underline"
                        >
                          Supprimer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {survey.questions?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-3">Questions</h2>
          <ul className="space-y-2">
            {survey.questions.map((q, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="text-gray-500">{i + 1}.</span>
                <span>{q.label}</span>
                <span className="text-xs text-gray-400">({q.type})</span>
                {q.is_main && <span className="text-xs bg-primary-100 text-primary-700 px-1 rounded">principal</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
